import React, { useEffect, useMemo, useState } from 'react';
import { Receipt, ChevronRight } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../ui/Card';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { formatArgentinaDate } from '../../utils/dateUtils';
import { PAYMENT_RECEIPT_NOTIFICATION_TITLE } from '../../utils/paymentReceipt';
import PaymentReceiptPreviewModal from '../modals/PaymentReceiptPreviewModal';

interface ReceiptNotification {
  id: string;
  title: string;
  message: string;
  action_url: string | null;
  created_at: string;
  is_read: boolean;
}

const PaymentReceiptsCard: React.FC = () => {
  const { user, isAdmin, userRole } = useAuth();
  const isAdminUser = isAdmin || userRole === 'Super Admin';
  const [items, setItems] = useState<ReceiptNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<{ url: string; subtitle: string } | null>(null);

  useEffect(() => {
    if (!user || !isAdminUser) return;

    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('notifications')
          .select('id, title, message, action_url, created_at, is_read')
          .eq('user_id', user.id)
          .ilike('title', `%${PAYMENT_RECEIPT_NOTIFICATION_TITLE}%`)
          .order('created_at', { ascending: false })
          .limit(8);

        if (error) throw error;
        setItems((data || []) as ReceiptNotification[]);
      } catch (e) {
        console.error('Error loading payment receipt notifications:', e);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user, isAdminUser]);

  const unreadCount = useMemo(() => items.filter((i) => !i.is_read).length, [items]);

  if (!isAdminUser) return null;

  const handleClick = async (item: ReceiptNotification) => {
    if (!item.action_url) return;
    setPreview({ url: item.action_url, subtitle: item.message });
    if (!item.is_read) {
      await supabase.from('notifications').update({ is_read: true }).eq('id', item.id);
      setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, is_read: true } : n)));
    }
  };

  return (
    <>
      <Card className="rounded-2xl border border-emerald-200/80 dark:border-emerald-800/50">
        <CardHeader
          title="Comprobantes de pago"
          icon={<Receipt className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />}
          action={
            unreadCount > 0 ? (
              <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                {unreadCount} nuevo{unreadCount === 1 ? '' : 's'}
              </span>
            ) : undefined
          }
        />
        <CardContent className="p-0">
          {loading ? (
            <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">Cargando…</p>
          ) : items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              No hay comprobantes recientes
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700/80">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => handleClick(item)}
                    disabled={!item.action_url}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                      !item.is_read ? 'bg-emerald-50/50 dark:bg-emerald-900/10' : ''
                    } ${!item.action_url ? 'opacity-60 cursor-default' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!item.is_read ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                        {item.message}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {formatArgentinaDate(item.created_at)}
                      </p>
                    </div>
                    {item.action_url && <ChevronRight className="h-4 w-4 text-gray-400 shrink-0 mt-1" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <PaymentReceiptPreviewModal
        isOpen={Boolean(preview)}
        onClose={() => setPreview(null)}
        receiptUrl={preview?.url ?? null}
        subtitle={preview?.subtitle}
      />
    </>
  );
};

export default PaymentReceiptsCard;
